import React, { useEffect, useState } from 'react';
import {
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Table,
  TableContainer,
  Paper,
  Checkbox,
} from '@mui/material';
import {
  Check as CheckIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useHistory, useLocation, useParams } from 'react-router';
import Page from '../../../../../components/Layout/Page';
import CircularProgressComponent from '../../../../../components/Loading/CircularProgressComponent';
import CustomButton from '../../../../../components/General/CustomButton';
import handleApiResponse from '../../../../../utils/handleApiResponse';
import { getTestsQuestionsByStudentId, postStudentAnswers, putStudentSavedAnswers } from '../../../../../requests/api/students';
import StudentTestDetailHeader from './Components/TestDetailHeader';
import HeaderSummaryAnswers from './Components/HeaderSummaryAnswers';
import useStyles from './styles';

interface LocationState {
  status: boolean;
  testName: string;
  programName: string;
}

type TestQuestions = {
  id: string;
  questionNumber: string;
  studentTestQuestions: {
    isCorrect: boolean;
    answer: string;
  }[];
  question: {
    correctAnswer: string;
    questionImageUrl: string;
  };
};

const numberToAnswerMap = {
  0: 'A',
  1: 'B',
  2: 'C',
  3: 'D',
  4: 'E',
};

const answerToNumberMap = {
  A: 0,
  B: 1,
  C: 2,
  D: 3,
  E: 4,
};

const TestAttempt = () => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const { id: studentId, testId } = useParams<{ id: string; testId: string }>();
  const location = useLocation<LocationState>();
  const testName = location.state ? location.state.testName : 'S/I';
  const programName = location.state ? location.state.programName : 'S/I';
  const history = useHistory();

  const [testLoading, setTestLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [selectedAnswer, setSelectedAnswer] = useState(Array(5).fill(false));
  const [userAnswers, setUserAnswers] = useState<{
    [key: string]: { answer: string | undefined; markedForRevision: boolean };
  }>({});
  const [testQuestions, setTestQuestions] = useState<TestQuestions[]>([]);
  const [answerInformation, setAnswerInformation] = useState({
    isAnswered: false,
    isSaved: false,
  });
  const [correctAnswers, setCorrectAnswers] = useState(0);

  useEffect(() => {
    const getAllTestsQuestionsFromStudent = async () => {
      try {
        const {
          testQuestions: questions,
          answeredInformation,
        } = await getTestsQuestionsByStudentId(
          studentId,
          testId,
        );

        setTestQuestions(questions);
        setAnswerInformation({
          isAnswered: answeredInformation.studentTests[0]?.answered || false,
          isSaved: answeredInformation.studentTests[0]?.saved || false,
        });

        const auxUserAnswers: {
          [key: string]: { answer: string | undefined; markedForRevision: boolean },
        } = {};

        if (answeredInformation.studentTests[0]?.answered) {
          questions.forEach((question: TestQuestions) => {
            auxUserAnswers[question.questionNumber] = {
              answer: question.studentTestQuestions[0]?.answer || undefined,
              markedForRevision: !(question.studentTestQuestions[0]?.answer && question.studentTestQuestions[0]?.answer !== 'O'),
            };
          });
          const correctCount = questions.reduce((acc: number, val: TestQuestions) => {
            const isCorrect = val.studentTestQuestions[0]?.isCorrect ? 1 : 0;
            return acc + isCorrect;
          }, 0);
          setCorrectAnswers(correctCount);
        } else if (answeredInformation.studentTests[0]?.saved) {
          questions.forEach((question: TestQuestions) => {
            auxUserAnswers[question.questionNumber] = {
              answer: question.studentTestQuestions[0]?.answer || undefined,
              markedForRevision: !(question.studentTestQuestions[0]?.answer && question.studentTestQuestions[0]?.answer !== 'O'),
            };
          });
        } else {
          questions.forEach((question: TestQuestions) => {
            auxUserAnswers[question.questionNumber] = {
              answer: undefined,
              markedForRevision: false,
            };
          });
        }

        setUserAnswers(auxUserAnswers);
        setTestLoading(false);
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
        setTestLoading(false);
      }
    };

    getAllTestsQuestionsFromStudent();
  }, []);

  const handleAnswerChange = (selection: number) => {
    if (answerInformation.isAnswered) return;

    const auxSelectedAnswer = Array(5).fill(false);
    if (!selectedAnswer[selection]) {
      auxSelectedAnswer[selection] = true;
    }
    setSelectedAnswer(auxSelectedAnswer);

    const answerFromUser = auxSelectedAnswer.indexOf(true);
    const formattedAnswer = answerFromUser === -1
      ? undefined
      : numberToAnswerMap[answerFromUser as 0 | 1 | 2 | 3];

    setUserAnswers((prevState) => ({
      ...prevState,
      [currentQuestion]: {
        ...prevState[currentQuestion],
        answer: formattedAnswer,
      },
    }));
  };

  const showSelectedCheckboxAnswerByUser = (relevantQuestion: number) => {
    const auxSelectedAnswer = userAnswers[relevantQuestion]?.answer;
    const mappedSelectedAnswer = auxSelectedAnswer ? answerToNumberMap[auxSelectedAnswer as 'A' | 'B' | 'C' | 'D' | 'E'] : null;

    if (mappedSelectedAnswer !== null) {
      const answersArray = Array(5).fill(false);
      answersArray[mappedSelectedAnswer] = true;
      setSelectedAnswer(answersArray);
    } else {
      setSelectedAnswer(Array(5).fill(false));
    }
  };

  const setQuestionForRevision = () => {
    if (answerInformation.isAnswered) return;

    setUserAnswers((prevState) => ({
      ...prevState,
      [currentQuestion]: {
        ...prevState[currentQuestion],
        markedForRevision: !prevState[currentQuestion]?.markedForRevision,
      },
    }));
  };

  const handlePrevNextQuestion = (source: string) => {
    const direction = source === 'n' ? 1 : -1;
    const newQuestion = currentQuestion + direction;
    if (newQuestion >= 1 && newQuestion <= testQuestions.length) {
      setCurrentQuestion(newQuestion);
      showSelectedCheckboxAnswerByUser(newQuestion);
    }
  };

  const handleClickQuestion = (questionToChoose: number) => {
    if (questionToChoose !== currentQuestion) {
      setCurrentQuestion(questionToChoose);
      showSelectedCheckboxAnswerByUser(questionToChoose);
    }
  };

  const formatAnswersToSend = () => Object.entries(userAnswers).map(([questionNumber, answer]) => ({
    testQuestionId: testQuestions[parseInt(questionNumber, 10) - 1].id,
    questionNumber,
    answer: answer.answer || 'O',
  }));

  const handleSaveAnswers = async () => {
    try {
      const response = await putStudentSavedAnswers({
        studentId,
        testId,
        answers: formatAnswersToSend(),
      });
      handleApiResponse(enqueueSnackbar, response, true);
      history.replace({ pathname: `/alumnos/${studentId}`, state: { activeTab: 2 } });
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    }
  };

  const handleSubmitTest = async () => {
    try {
      const response = await postStudentAnswers({
        studentId,
        testId,
        answers: formatAnswersToSend(),
      });
      handleApiResponse(enqueueSnackbar, response, true);
      history.replace({ pathname: `/alumnos/${studentId}`, state: { activeTab: 2 } });
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    }
  };

  const setSpecificClassTableAnswer = (numberQuestion: string) => {
    if (!userAnswers[numberQuestion]?.answer) {
      return classes.tableRowAnswerNotAnswered;
    }
    return '';
  };

  const setSpecificIconTableAnswer = (
    answer: { answer: string | undefined, markedForRevision: boolean },
  ) => {
    if (answer?.answer && !answer.markedForRevision) {
      return <CheckIcon />;
    }
    if (answer?.markedForRevision) {
      return <WarningIcon />;
    }
    return '';
  };

  if (testLoading) {
    return <CircularProgressComponent />;
  }

  return (
    <Page title="Alumnos | Detalle guia">
      <StudentTestDetailHeader
        status={answerInformation.isAnswered}
        testName={testName}
        programName={programName}
        handleSaveAnswers={handleSaveAnswers}
        handleSubmitTest={handleSubmitTest}
      />
      {answerInformation.isAnswered && (
        <HeaderSummaryAnswers correctAnswers={`${correctAnswers}/${testQuestions.length}`} />
      )}
      <Box className={classes.questionsContainer}>
        <Box>
          <Paper className={classes.tablePaperContainer}>
            <TableContainer className={classes.tableQuestionAnswers}>
              <Table style={{ tableLayout: 'fixed' }} stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableCellPrimaryBackgroundColor}>
                      Número de pregunta
                    </TableCell>
                    <TableCell className={classes.tableCellPrimaryBackgroundColor}>
                      Respuesta
                    </TableCell>
                    <TableCell className={classes.tableCellPrimaryBackgroundColor}>
                      Por revisar
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(userAnswers).map((elem) => (
                    <TableRow
                      key={elem[0]}
                      onClick={() => handleClickQuestion(parseInt(elem[0], 10))}
                      className={`${classes.tableRowAnswerBase} ${setSpecificClassTableAnswer(elem[0])} ${parseInt(elem[0], 10) === currentQuestion ? classes.currentQuestion : ''}`}
                    >
                      <TableCell>{elem[0]}</TableCell>
                      <TableCell>{elem[1]?.answer || '-'}</TableCell>
                      <TableCell>
                        {setSpecificIconTableAnswer(elem[1])}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
        <Box className={classes.questionAndButtonsContainer}>
          <Box display='flex' className={classes.questionContainer}>
            <img src={testQuestions[currentQuestion - 1].question.questionImageUrl} width='100%' style={{ height: 'fit-content', width: 'auto', maxWidth: '100%' }} alt={`Question ${currentQuestion}`} />
          </Box>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{(window as Window).innerWidth > 900 ? 'Número de pregunta' : 'N°'}</TableCell>
                  <TableCell>A</TableCell>
                  <TableCell>B</TableCell>
                  <TableCell>C</TableCell>
                  <TableCell>D</TableCell>
                  <TableCell>E</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{currentQuestion}</TableCell>
                  <TableCell>
                    <Checkbox
                      checked={selectedAnswer[0]}
                      onChange={() => handleAnswerChange(0)}
                      disabled={answerInformation.isAnswered}
                    />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={selectedAnswer[1]}
                      onChange={() => handleAnswerChange(1)}
                      disabled={answerInformation.isAnswered}
                    />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={selectedAnswer[2]}
                      onChange={() => handleAnswerChange(2)}
                      disabled={answerInformation.isAnswered}
                    />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={selectedAnswer[3]}
                      onChange={() => handleAnswerChange(3)}
                      disabled={answerInformation.isAnswered}
                    />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={selectedAnswer[4]}
                      onChange={() => handleAnswerChange(4)}
                      disabled={answerInformation.isAnswered}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Box className={classes.buttons}>
            <CustomButton
              text={userAnswers[currentQuestion]?.markedForRevision ? 'Quitar de revisión' : 'Revisar después'}
              colorType="warning"
              onClick={setQuestionForRevision}
              icon={<WarningIcon />}
              disabled={answerInformation.isAnswered}
            />
            <Box display='flex'>
              <CustomButton
                text="Anterior"
                colorType="primary"
                onClick={() => handlePrevNextQuestion('p')}
                icon={<ChevronLeftIcon />}
                disabled={currentQuestion === 1}
              />
              <CustomButton
                text="Siguiente"
                colorType="primary"
                onClick={() => handlePrevNextQuestion('n')}
                suffixIcon={<ChevronRightIcon />}
                disabled={currentQuestion === testQuestions.length}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Page>
  );
};

export default TestAttempt;
